/**
 * Background removal via @huggingface/transformers + RMBG-1.4.
 * Built to wwwroot/filters/background/removeBackgroundAi.js (IIFE globals).
 */
import {
    AutoModel,
    AutoProcessor,
    RawImage,
    Tensor,
    env,
} from "@huggingface/transformers";
import { ensureOrtWebGpuAdapterInfo, preferredOnnxDevices, rmbgDtypeForDevice } from "./webgpuDevice.js";

const MODEL_ID = "briaai/RMBG-1.4";
const MODEL_SIZE = 1024;
const ORT_WASM_PATH = "/lib/onnxruntime-web-hf/";

configureTransformersEnv();

let modelLoaded = false;
let modelLoadPromise = null;
let startButtonBound = false;
let status = null;
/** @type {Awaited<ReturnType<typeof AutoModel.from_pretrained>> | null} */
let model = null;
/** @type {Awaited<ReturnType<typeof AutoProcessor.from_pretrained>> | null} */
let processor = null;

const dummyInput = new Float32Array(1 * 3 * MODEL_SIZE * MODEL_SIZE);

function configureTransformersEnv() {
    if (!navigator.gpu) {
        console.warn("WebGPU is not supported by this browser. Background removal will use WASM.");
    }
    ensureOrtWebGpuAdapterInfo();

    env.allowLocalModels = true;
    env.localModelPath = "/models/";
    env.allowRemoteModels = true;
    env.backends.onnx.wasm.wasmPaths = ORT_WASM_PATH;
    env.backends.onnx.wasm.numThreads = 1;
    env.backends.onnx.wasm.proxy = false;
}

function backgroundModelReady() {
    return modelLoaded;
}

async function loadBackgroundModel() {
    if (modelLoaded) {
        if (status) status.textContent = "";
        return;
    }

    if (typeof beginFilterProcessingWait === "function") {
        await beginFilterProcessingWait(LOADING_AI_MODELS, LOADING_AI_MODELS_BODY, { immediate: true });
    }
    try {
        if (modelLoadPromise) {
            return await modelLoadPromise;
        }

        modelLoadPromise = loadBackgroundModelImpl();
        try {
            await modelLoadPromise;
        } finally {
            if (!modelLoaded) modelLoadPromise = null;
        }
    } finally {
        if (typeof endFilterProcessingWait === "function") {
            endFilterProcessingWait();
        }
    }
}

async function loadBackgroundModelImpl() {
    status = document.getElementById("processingStatus");
    if (status) status.textContent = "Loading model...";

    const startButton = document.getElementById("startRemoveBackground");
    if (startButton && !startButtonBound) {
        startButtonBound = true;
        startButton.addEventListener("click", async () => {
            if (typeof currentFilter !== "undefined" && currentFilter === AI_BATCH_REMOVE_BACKGROUND) {
                return;
            }
            await runBackgroundRemoval(null);
        });
    }

    const devices = await preferredOnnxDevices();
    let lastError = null;
    for (const device of devices) {
        try {
            model = await AutoModel.from_pretrained(MODEL_ID, {
                config: { model_type: "custom" },
                dtype: rmbgDtypeForDevice(device),
                device,
            });
            lastError = null;
            break;
        } catch (err) {
            lastError = err;
            console.warn(`Background model ${device} init failed:`, err);
            model = null;
        }
    }

    if (!model) {
        const message = lastError?.message || String(lastError || "Failed to load background model.");
        if (status) status.textContent = message;
        alert(message);
        throw lastError || new Error(message);
    }

    processor = await AutoProcessor.from_pretrained(MODEL_ID, {
        config: {
            do_normalize: true,
            do_pad: false,
            do_rescale: true,
            do_resize: true,
            image_mean: [0.5, 0.5, 0.5],
            feature_extractor_type: "ImageFeatureExtractor",
            image_std: [1, 1, 1],
            resample: 2,
            rescale_factor: 1 / 255,
            size: { width: MODEL_SIZE, height: MODEL_SIZE },
        },
    });

    await model({
        input: new Tensor("float32", dummyInput, [1, 3, MODEL_SIZE, MODEL_SIZE]),
    });

    if (status) status.textContent = "Ready";
    modelLoaded = true;
}

async function runBackgroundRemoval(imageData) {
    if (!backgroundModelReady()) {
        return false;
    }

    if (!imageData) {
        imageData = typeof getWorkingImageData === "function" ? getWorkingImageData() : null;
    }
    if (!imageData) {
        return false;
    }

    const useWaitModal = typeof beginFilterProcessingWait === "function"
        && typeof endFilterProcessingWait === "function";
    if (useWaitModal) {
        const isRemoveBackground = typeof currentFilter !== "undefined" && currentFilter === AI_REMOVE_BACKGROUND;
        await beginFilterProcessingWait(
            isRemoveBackground ? REMOVING_BACKGROUND_TAKING_LONG : APPLYING_FILTER,
            isRemoveBackground ? AI_MAY_TAKE_LONGER : null,
            { immediate: true }
        );
    }

    try {
        const out = await predict(imageData);
        if (!out) {
            return false;
        }
        if (typeof removeBackground !== "function") {
            throw new Error("removeBackground is not available.");
        }
        removeBackground(imageData, out);
        return true;
    } catch (err) {
        if (status) status.textContent = "Background removal failed";
        console.error("Background removal failed:", err);
        return false;
    } finally {
        if (useWaitModal) {
            endFilterProcessingWait();
        }
    }
}

async function predict(imageData) {
    if (!modelLoaded || !model || !processor) {
        if (status) status.textContent = "Model not loaded";
        return null;
    }
    if (!imageData) {
        return null;
    }
    if (typeof copyImageData !== "function") {
        throw new Error("copyImageData is not available.");
    }

    const width = imageData.width;
    const height = imageData.height;
    const out = copyImageData(imageData);
    const rawImage = new RawImage(imageData.data, width, height, 4);

    if (status) status.textContent = "Analysing...";

    const { pixel_values } = await processor(rawImage);
    const { output } = await model({ input: pixel_values });
    const mask = await RawImage.fromTensor(output[0].mul(255).to("uint8")).resize(width, height);

    const pixelCount = Math.min(mask.data.length, (out.data.length / 4) | 0);
    for (let i = 0; i < pixelCount; i++) {
        out.data[i * 4 + 3] = mask.data[i];
    }

    if (status) status.textContent = "Done!";
    return out;
}

Object.assign(globalThis, {
    backgroundModelReady,
    loadBackgroundModel,
    runBackgroundRemoval,
    predict,
});
